import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';
import { requireGroupId, extractError } from '../../store/thunkHelpers';
import { whatsAppMessageApi } from './whatsAppMessageApi';
import { whatsAppSendErrorFrom } from './whatsAppMessageMappers';
import type {
  WhatsAppMediaSendPayload,
  WhatsAppMessage,
  WhatsAppMessageSendPayload,
  WhatsAppNumber,
  WhatsAppSendError,
  WhatsAppTemplate,
  WhatsAppTemplateSendPayload,
} from './whatsAppMessageTypes';

/**
 * Invio outbound di un messaggio WhatsApp a un contatto (POST
 * /whatsapp-messages/{groupId}/send). È l'<b>unica</b> scrittura dal CRM: gli
 * inbound nascono dal webhook Meta e la lettura avviene per <i>sessione</i>
 * (entità whatsAppSession), non per singolo messaggio — perciò qui non serve né
 * lista né slice, solo questo thunk. Il groupId è risolto dallo stato.
 *
 * L'errore non è una semplice stringa: se il backend rifiuta con 409 (finestra di
 * 24h chiusa) il conflitto viaggia strutturato, così il composer può proporre un
 * template invece di mostrare un errore.
 */
export const sendWhatsAppMessage = createAsyncThunk<
  WhatsAppMessage,
  WhatsAppMessageSendPayload,
  { state: RootState; rejectValue: WhatsAppSendError }
>('whatsAppMessages/send', async (payload, { getState, rejectWithValue }) => {
  try {
    return await whatsAppMessageApi.create(requireGroupId(getState), payload);
  } catch (err: unknown) {
    return rejectWithValue(whatsAppSendErrorFrom(err));
  }
});

/**
 * Invio outbound di un <b>media</b> (file) a un contatto (POST multipart
 * /whatsapp-messages/{groupId}/send-media). La caption riusa il testo del composer.
 *
 * A finestra chiusa non c'è ripiego: un media può uscire solo dentro un template
 * con header media approvato, che è un'altra cosa. Il 409 arriva comunque
 * strutturato, così la UI spiega il rifiuto.
 */
export const sendWhatsAppMediaMessage = createAsyncThunk<
  WhatsAppMessage,
  WhatsAppMediaSendPayload,
  { state: RootState; rejectValue: WhatsAppSendError }
>(
  'whatsAppMessages/sendMedia',
  async (payload, { getState, rejectWithValue }) => {
    try {
      return await whatsAppMessageApi.sendMedia(
        requireGroupId(getState),
        payload,
      );
    } catch (err: unknown) {
      return rejectWithValue(whatsAppSendErrorFrom(err));
    }
  },
);

/**
 * Invio di un template approvato (POST /whatsapp-messages/{groupId}/send-template):
 * l'unico messaggio che Meta accetta quando la finestra di 24h è chiusa. Non
 * riapre la finestra — quella la riapre solo una risposta del contatto.
 */
export const sendWhatsAppTemplateMessage = createAsyncThunk<
  WhatsAppMessage,
  WhatsAppTemplateSendPayload,
  { state: RootState; rejectValue: WhatsAppSendError }
>(
  'whatsAppMessages/sendTemplate',
  async (payload, { getState, rejectWithValue }) => {
    try {
      return await whatsAppMessageApi.sendTemplate(
        requireGroupId(getState),
        payload,
      );
    } catch (err: unknown) {
      return rejectWithValue(whatsAppSendErrorFrom(err));
    }
  },
);

/**
 * Trascrizione di una nota vocale (POST /whatsapp-message/{id}/transcribe).
 *
 * Il messaggio aggiornato non finisce in uno slice suo — non esiste: i messaggi
 * vivono annidati dentro le sessioni. Lo raccoglie `whatsAppSessionSlice`, che
 * sostituisce il messaggio dentro la sessione che lo contiene. Così la trascrizione
 * sopravvive alla chiusura dell'accordion, senza doverla richiedere di nuovo.
 */
export const transcribeWhatsAppMessage = createAsyncThunk<
  WhatsAppMessage,
  number,
  { state: RootState; rejectValue: string }
>(
  'whatsAppMessages/transcribe',
  async (whatsAppMessageId, { rejectWithValue }) => {
    try {
      return await whatsAppMessageApi.transcribe(whatsAppMessageId);
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  },
);

/**
 * Numeri WhatsApp business mittente configurati per la company: popolano la combo
 * di scelta del mittente nel modale di invio (TS-89).
 */
export const fetchWhatsAppNumbers = createAsyncThunk<
  WhatsAppNumber[],
  void,
  { state: RootState; rejectValue: string }
>('whatsAppMessages/fetchNumbers', async (_, { getState, rejectWithValue }) => {
  try {
    return await whatsAppMessageApi.getNumbers(requireGroupId(getState));
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

/** Template approvati sul WhatsApp Business Account, letti live da Meta. */
export const fetchWhatsAppTemplates = createAsyncThunk<
  WhatsAppTemplate[],
  void,
  { state: RootState; rejectValue: string }
>(
  'whatsAppMessages/fetchTemplates',
  async (_, { getState, rejectWithValue }) => {
    try {
      return await whatsAppMessageApi.getTemplates(requireGroupId(getState));
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  },
);
